import { supabase } from '@/integrations/supabase/client';

/**
 * Small typed shims around Supabase calls that target tables / RPCs not
 * present in the generated `Database` type. Centralising them here keeps
 * the `as unknown as (...)` casts out of feature code and gives us one
 * place to adjust when the generated types catch up.
 *
 * Prefer these helpers over re-casting `supabase.rpc` / `supabase.from`
 * at call sites. If you need a shape not covered here, add a function
 * rather than inlining another cast.
 */

type RpcCall<T> = Promise<{ data: T | null; error: Error | null }>;

/**
 * Invoke a Postgres function not present in the generated Database type.
 * `T` is the shape you expect back (or `boolean` / primitives).
 *
 * IMPORTANT: call `supabase.rpc` as a METHOD on the client instance —
 * extracting it as a plain function reference breaks the internal `this`
 * binding (supabase-js reads `this.rest` internally, which becomes
 * undefined and throws "Cannot read properties of undefined (reading
 * 'rest')"). Keep the call-chain intact.
 */
export function callRpc<T>(fn: string, args?: Record<string, unknown>): RpcCall<T> {
  return (supabase.rpc as unknown as (
    name: string,
    argv?: Record<string, unknown>,
  ) => RpcCall<T>).call(supabase, fn, args);
}

interface SelectBuilder<Row> {
  select: (columns: string) => this;
  eq: (column: string, value: unknown) => this;
  in: (column: string, values: unknown[]) => this;
  gte: (column: string, value: unknown) => this;
  lte: (column: string, value: unknown) => this;
  lt: (column: string, value: unknown) => this;
  gt: (column: string, value: unknown) => this;
  ilike: (column: string, pattern: string) => this;
  or: (filter: string) => this;
  order: (column: string, options: { ascending: boolean }) => this;
  range: (from: number, to: number) => Promise<{ data: Row[] | null; error: Error | null }>;
  limit: (n: number) => Promise<{ data: Row[] | null; error: Error | null }>;
  single: () => Promise<{ data: Row | null; error: Error | null }>;
  maybeSingle: () => Promise<{ data: Row | null; error: Error | null }>;
}

interface UpsertBuilder {
  upsert: (row: Record<string, unknown>, opts?: { onConflict?: string }) =>
    Promise<{ error: Error | null }>;
  insert: (row: Record<string, unknown>) => Promise<{ error: Error | null }>;
  update: (patch: Record<string, unknown>) => {
    eq: (column: string, value: unknown) => Promise<{ error: Error | null }>;
  };
}

/**
 * Query a table that isn't in the generated Database type. Returns a
 * builder with the common `.select().eq().order().limit()` surface plus
 * insert/upsert/update shortcuts.
 *
 * Called as a method on `supabase` directly — see callRpc above for the
 * same `this`-binding reasoning.
 */
export function fromCustom<Row = Record<string, unknown>>(
  table: string,
): SelectBuilder<Row> & UpsertBuilder {
  const client = supabase as unknown as {
    from: (t: string) => SelectBuilder<Row> & UpsertBuilder;
  };
  return client.from(table);
}
